import TriangleRightIcon from "@redotech/redo-icons/play_right_filled.svg";
import TriangleDownIcon from "@redotech/redo-icons/triangle-down_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classnames from "classnames";
import type { HTMLAttributes, ReactNode } from "react";
import { memo, useState } from "react";
import * as styles from "./redo-content-card.module.css";

export type RedoContentCardSpacing =
  | "none"
  | "compact"
  | "default"
  | "spacious";

const paddingBySpacing = {
  none: "none",
  compact: "xl",
  default: "2xl",
  spacious: "3xl",
} as const;

export interface RedoContentCardProps {
  background?: "primary" | "tertiary";
  children: ReactNode;
  contentSpacing?: RedoContentCardSpacing;
  defaultExpanded?: boolean;
  headerActions?: ReactNode;
  headerSpacing?: RedoContentCardSpacing;
  reorderHandleProps?: HTMLAttributes<HTMLDivElement>;
  showReorderHandle?: boolean;
  subtitle?: ReactNode;
  title: ReactNode;
}

export type RedoContentCardReorderProps = Pick<
  RedoContentCardProps,
  "reorderHandleProps" | "showReorderHandle"
>;

export const RedoContentCard = memo(function RedoContentCard({
  background = "primary",
  children,
  contentSpacing = "spacious",
  defaultExpanded = true,
  headerActions,
  headerSpacing = "compact",
  reorderHandleProps,
  showReorderHandle = false,
  subtitle,
  title,
}: RedoContentCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { className: reorderHandleClassName, ...restReorderHandleProps } =
    reorderHandleProps ?? {};

  return (
    <Flex
      bgColor={background}
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      flexDirection="column"
      gap="none"
      radius="md"
      w="full"
    >
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        className={styles.cardHeader}
        justify="space-between"
        p={paddingBySpacing[headerSpacing]}
      >
        <Flex
          align="center"
          className={classnames(
            styles.titleArea,
            showReorderHandle && styles.titleAreaWithHandle,
          )}
          minW="none"
        >
          {showReorderHandle && (
            <Flex
              {...restReorderHandleProps}
              aria-label="Reorder card"
              className={classnames(
                styles.reorderHandle,
                reorderHandleClassName,
              )}
              role="button"
              tabIndex={0}
            />
          )}
          <Flex flexDirection="column" gap="xs" minW="none">
            {typeof title === "string" ? (
              <Text color="secondary" fontSize="sm" fontWeight="medium">
                {title}
              </Text>
            ) : (
              title
            )}
            {subtitle}
          </Flex>
        </Flex>
        <Flex align="center" gap="md">
          {headerActions}
          <RedoButton
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse card" : "Expand card"}
            hierarchy="tertiary"
            IconLeading={isExpanded ? TriangleDownIcon : TriangleRightIcon}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            size="sm"
            type="button"
          />
        </Flex>
      </Flex>
      <Flex
        display={isExpanded ? "flex" : "none"}
        flexDirection="column"
        gap="none"
        p={paddingBySpacing[contentSpacing]}
        w="full"
      >
        {children}
      </Flex>
    </Flex>
  );
});
